/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import Swiper, {
	Autoplay,
	Navigation,
	Pagination,
	Parallax,
	Scrollbar,
} from "swiper";
import MicroModal from "micromodal";

// FRAMEWORKS : GSAP BUSINESS
import "../js/gsap-business-green/gsap.min.js";
import "../js/gsap-business-green/EasePack.min.js";
import "../js/gsap-business-green/ScrollSmoother.min.js";
import "../js/gsap-business-green/ScrollTrigger.min.js";
import "../js/gsap-business-green/ScrollToPlugin.min.js";
import { SplitText } from "../js/gsap-business-green/SplitText.min.js";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// ScrollSmoother (GSAP)
let scrollSmooth = ScrollSmoother.create({
	wrapper: "#page-wrapper",
	content: "#page-main",
	smooth: 1,
	smoothTouch: false,
	effects: true,
	normalizeScroll: false,
	ignoreMobileResize: true,
});

// Élèment à animer (en CSS ou JS)
const elementsToAnimated = document.querySelectorAll([
	// global
	"[data-animation]",
	// elementor
	".page__article .elementor-button-wrapper",
	".page__article .elementor-heading-title",
	".page__article .elementor-icon-list-item",
	".page__article .elementor-icon-wrapper",
	".page__article .elementor-sitemap-section > *",
	".page__article .elementor-widget-html",
	".page__article .elementor-widget-image",
	".page__article .elementor-widget-nav-menu",
	".page__article .elementor-widget-text-editor",
	".page__article .elementor-widget-video",
	".page__article .elementor-shortcode",
]);
const textsToAnimated = document.querySelectorAll([
	// global
	"[data-animation='reveal-text']",
	".page__article .elementor-heading-title",
]);

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("no-js");
	// Preloader (et gestion des animations GSAP)
	gsapPreLoader();
	// Gestion des menus
	headerScrolledMenu(160);
	headerMobileMenu();
	menuItemDropdown();
	// Gestion de la bannière
	bannerClosing();
	bannerScrollingDetect();
	// Gestion des "data" custom
	popupInit();
	toggleInit();
	// Gestion des sliders
	allSwiperInit();
	// Gestion du cache
	sessionStorage.setItem("visited", true);
});

// DOCUMENT RESIZED
ScrollTrigger.addEventListener("refreshInit", () => {
	// Actualisation du smooth scroll
	gsapScrollSmootherState("refresh");
	// Actualisation de la bannière
	bannerScrollingDetect();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Détection du scroll
function headerScrolledMenu(scrollOffset) {
	let position = window.pageYOffset;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// detect scroll direction
		let scroll = window.pageYOffset;
		if (scroll > position) {
			// if scroll to bottom
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// if scroll to top
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// if scroll max reached
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
}

// Menu Burger : Comportement au clic
function headerMobileMenu() {
	const pageHeaderBurger = document.querySelector(
		"#page-header #page-header-burger"
	);
	const pageHeader = document.querySelector("#page-header");

	pageHeaderBurger.addEventListener("click", () => {
		if (pageHeader.classList.contains("page__header--open")) {
			// Fermer le menu au clic sur le bouton burger
			document.documentElement.style.overflowY = "auto";
			if (scrollSmooth) {
				gsapScrollSmootherState("play");
			}
			pageHeader.classList.remove("page__header--open");
		} else {
			// Ouvrir le menu au clic sur le bouton burger
			document.documentElement.style.overflowY = "hidden";
			if (scrollSmooth) {
				gsapScrollSmootherState("pause");
			}

			pageHeader.classList.add("page__header--open");
		}
	});

	document.addEventListener("mouseup", (event) => {
		// Fermer le menu au clic dehors de la zone
		if (
			!pageHeader.contains(event.target) &&
			event.target !== pageHeader &&
			!event.target.closest("#page-header")
		) {
			document.documentElement.style.overflowY = "auto";
			if (scrollSmooth) {
				gsapScrollSmootherState("play");
			}
			pageHeader.classList.remove("page__header--open");
		}
	});
}

// Menu Dropdown : Comportement au clic
function menuItemDropdown() {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
}

// Ouverture/Fermeture de la bannière
function bannerClosing() {
	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");
		});
	}
}

// Activation auto du défilement horizontal
function bannerScrollingDetect() {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
}

// Popup : Comportement au clic
function popupInit() {
	const popups = document.querySelectorAll("[data-popup]");

	popups.forEach((popup) => {
		popup.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popup
			const thisPopupName = popup.dataset.popup;
			if (thisPopupName) {
				const thisPopupEl = document.querySelector(
					`.page__popup#${thisPopupName}`
				);
				thisPopupEl.classList.toggle("page__popup--open");
			}

			// mettre les vidéos des popups en pause
			const htmlVideoFromPopup =
				document.querySelectorAll(".page__popup video");
			if (htmlVideoFromPopup.length > 0) {
				htmlVideoFromPopup.forEach((video) => {
					video.pause();
				});
			}
		});
	});

	document.addEventListener("mouseup", (event) => {
		// si une popup est ouverte
		if (
			document.querySelectorAll(".page__popup.page__popup--open").length > 0
		) {
			// click en dehors de la zone
			if (
				!document
					.querySelector(".page__popup .popup__wrapper")
					.contains(event.target) &&
				document.querySelector(".page__popup .popup__wrapper") !== event.target
			) {
				// fermer la popup
				document.querySelectorAll(".page__popup").forEach((popup) => {
					popup.classList.remove("page__popup--open");
				});

				// mettre les vidéos des popups en pause
				const htmlVideoFromPopup =
					document.querySelectorAll(".page__popup video");
				if (htmlVideoFromPopup.length > 0) {
					htmlVideoFromPopup.forEach((popupVideo) => {
						popupVideo.pause();
					});
				}
			}
		}
	});
}

// Toggle : Comportement au clic
function toggleInit() {
	const toggleListener = document.querySelectorAll("[data-toggle]");

	toggleListener.forEach((toggleButton) => {
		toggleButton.addEventListener("click", (event) => {
			// ouvrir/fermer l'accordéon
			toggleButton.classList.toggle(
				"active",
				!toggleButton.classList.contains("active")
			);
			let toggleData = toggleButton.dataset.toggle;
			if (toggleData) {
				toggleData = toggleData.split(",");
				let toggleTarget = toggleData.shift();
				if (toggleTarget) {
					// cible
					let toggleTargetEl = document.querySelector(`#${toggleTarget}`);
					if (toggleData) {
						toggleData.forEach(function (className) {
							className = className.trim();
							// ajouter/retirer les classes renseignés
							toggleTargetEl.classList.toggle(className);
						});
					} else {
						// ajouter/retirer la classe ".active"
						toggleTargetEl.classList.toggle("active");
					}
				}
			}
		});
	});
}

// Activation de Swiper.js (sliders)
function allSwiperInit() {
	if (
		document.querySelectorAll(
			".section-archive-related-news .swiper .swiper-slide"
		).length > 0
	) {
		const swiperRelatedNews = new Swiper(
			".section-archive-related-news .swiper",
			{
				// Core parameters
				slidesPerView: "auto",
				speed: 1200,
				resizeObserver: true,
				// Modules
				modules: [Autoplay, Scrollbar],
				// Modules parameters
				autoplay: {
					delay: 3600,
					disableOnInteraction: true,
				},
				scrollbar: {
					el: ".section-archive-related-news .swiper-scrollbar",
					draggable: true,
				},
			}
		);
	}

	if (
		document.querySelectorAll(
			".section-hero-single-portfolio .swiper .swiper-slide"
		).length > 0
	) {
		const swiperHeroSinglePortfolio = new Swiper(
			".section-hero-single-portfolio .swiper",
			{
				// Core parameters
				slidesPerView: "auto",
				speed: 1200,
				loop: true,
				resizeObserver: true,
				// Modules
				modules: [Autoplay, Navigation, Parallax],
				// Modules parameters
				autoplay: {
					delay: 2400,
					disableOnInteraction: true,
				},
				navigation: {
					nextEl: ".swiper-button-next",
					prevEl: ".swiper-button-prev",
				},
				parallax: true,
			}
		);
	} 

	if (document.querySelector(".galery")) {
		const swiperHotellerieGalery = new Swiper(".galery--hotellerie.swiper", {
			slidesPerView: "auto",
			spaceBetween: 2,
			modules: [Navigation, Pagination],
			pagination: {
				el: ".swiper-pagination",
				type: "bullets",
			},
			/*breakpoints: {
        992: {
          slidesPerView: 4
        },
      }*/
		});
		const swiperFonciereGalery = new Swiper(".galery--fonciere.swiper", {
			slidesPerView: 2,
			spaceBetween: 2,
			modules: [Navigation, Pagination],
			pagination: {
				el: ".swiper-pagination",
				type: "bullets",
			},
		});
		const swiperNewsGalery = new Swiper(".galery--news.swiper", {
			slidesPerView: 1,
			spaceBetween: 10,
			modules: [Navigation, Pagination],
			pagination: {
				el: ".swiper-pagination",
				type: "bullets",
			},
			breakpoints: {
				767: {
					slidesPerView: 2,
				},
				992: {
					slidesPerView: 3,
				},
			},
		});
	}
}

// État du scrollSmoother plugin
function gsapScrollSmootherState(state) {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		}
		// Rafraîchir sinon
		else {
			if (state === "refresh") {
				scrollSmooth.refresh();
			}
			// Mettre en pause
			if (state === "pause") {
				scrollSmooth.paused(true);
			}
			if (state === "play" && scrollSmooth.paused()) {
				scrollSmooth.paused(false);
			}
		}
	}
}

// Scroll vers une ancre
function gsapScrollTo() {
	// avec un [href="#..."]
	const scrollToAnchor = document.querySelectorAll("a[href^='#']");
	scrollToAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			event.preventDefault();
			let anchorHref = link.getAttribute("href");
			scrollSmooth.scrollTo(anchorHref, true);
		});
	});

	// avec un [data-scrollto]
	const scrollToData = document.querySelectorAll("[data-scrollto]");
	scrollToData.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData == "auto") {
				anchorData = window.innerHeight + window.scrollY;
			}
			scrollSmooth.scrollTo(anchorData, true);
		});
	});

	// au chargement
	let hashInUrl = window.location.hash;
	if (hashInUrl) {
		console.log(hashInUrl);
		scrollSmooth.scrollTo(hashInUrl, true);
	}
}

// Sticky element
function gsapStickyElement() {
	const stickyWrapper = document.querySelector("[data-sticky]");

	if (stickyWrapper) {
		ScrollTrigger.matchMedia({
			// desktop
			"(min-width: 992px)": () => {
				ScrollTrigger.create({
					trigger: stickyWrapper.firstElementChild,
					start: "center center",
					end: () =>
						`+=${
							stickyWrapper.offsetHeight -
							stickyWrapper.firstElementChild.offsetHeight
						}`,
					pin: true,
					pinSpacing: false,
					invalidateOnRefresh: true,
					// markers: true,
				});
			},
		});
	}
}

// Découpage des textes avec SplitText
function gsapSplitText() {
	textsToAnimated.forEach((_thisText) => {
		new SplitText(_thisText, {
			type: "lines, words", //, chars",
			linesClass: "line",
			wordsClass: "word",
			// charsClass: "letter",
		});
	});
}

// Animations avec le ScrollTrigger
function gsapAnimations() {
	// Ajout/retrait de la classe --animated
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "-=100px +=100%",
			end: "bottom +=0%",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	if (document.querySelector(".home-hero-menu")) {
		let homeHeroTL = gsap
			.timeline({ delay: 0.5, defaults: { duration: 1.5 } })
			.to(".masks path", { y: "0", stagger: 0.2 })
			.to(".lines path", { y: "0", stagger: 0.2 }, "<=+0.2")
			.to(".home-hero-menu a", { y: "0", stagger: 0.2 }, "<=+0.4");

		gsap.to(".logo path", { opacity: 1, y: "0%", stagger: 0.2, duration: 1 });

		document.querySelectorAll(".home-hero-menu a").forEach(function (a) {
			a.addEventListener("mouseover", function () {
				document
					.querySelectorAll(".home-hero-menu a.active")
					.forEach(function (b) {
						b.classList.remove("active");
					});
				a.classList.add("active");
			});
		});
	}
	if (document.querySelector(".flying-text")) {
		const rows = document.querySelectorAll(".flying-text .line");

		rows.forEach(function (e, i) {
			//let row_width = e.getBoundingClientRect().width;
			let row_width = 0;
			e.querySelectorAll("div").forEach(function (div) {
				row_width += div.getBoundingClientRect().width;

				let clone = div.cloneNode(true);
				e.append(clone);
			});
			e.style.width = row_width * 2 + "px";

			let clone = e.cloneNode(true);
			e.append(clone);

			let initial_offset = -50;

			gsap.set(e, {
				xPercent: `${initial_offset}`,
			});

			let duration = 10 * (i + 1);
			var tl = gsap.timeline();

			tl.to(e, {
				ease: "none",
				duration: duration,
				xPercent: 0,
				repeat: -1,
			});
		});
	}
	if (document.querySelector(".trident")) {
		if (document.querySelector(".trident") && window.innerWidth > 1300) {
			document.querySelectorAll(".trident").forEach((trident, i) => {
				const bgTL = gsap
					.timeline({ paused: true })
					.to(".home-domains .section__background", {
						background: "#968c88",
					})
					.to(".home-domains .section__background", {
						background: "#2b655f",
					})
					.to(".home-domains .section__background", {
						background: "#e8c26b",
					});

				const tridentTL = gsap
					.timeline({ paused: true })
					.to(".trident--fonciere .trident-mask", {
						opacity: 1,
					})
					.to(".trident--fonciere .trident-mask", {
						opacity: 0,
					})
					.to(".trident--promotion .trident-mask", {
						opacity: 0,
					});

				ScrollTrigger.create({
					trigger: ".home-domains",
					start: "top top",
					end: "bottom bottom",
					scrub: true,
					onUpdate: (self) => {
						bgTL.progress(self.progress);
						tridentTL.progress(self.progress);
					},
				});
				const push = trident.querySelector(".push");
				gsap.set(push, { opacity: 0, y: "100%" });
				const wrapper = trident.querySelector(".wrapper");
				gsap.set(wrapper, { opacity: 0, y: "100%" });

				ScrollTrigger.create({
					trigger: trident,
					start: "bottom bottom",
					endTrigger: ".home-domains",
					end: `bottom bottom`,
					pin: true,
					pinSpacing: false,
					id: "pin",
					invalidateOnRefresh: true,
					onUpdate: (self) => {
						if (self.progress > 0) {
							gsap.to(push, { opacity: 1, y: 0 });
							gsap.to(wrapper, { opacity: 1, y: 0, delay: 0.2 });
						}
					},
				});
			});
		}
	}

	if (document.querySelector(".home__join")) {
		document
			.querySelectorAll(".home__join .activity")
			.forEach((activity, i) => {
				if (
					document.querySelectorAll(".home__join .activity").length !=
					i + 1
				) {
					ScrollTrigger.create({
						trigger: activity,
						start: `top-=${i * 150} top`,
						endTrigger: ".activity--hotellerie",
						end: `top 450`,
						pin: true,
						pinSpacing: false,
						id: "pin",
						invalidateOnRefresh: true,
					});
				}
			});
	}

	if (document.querySelector(".home__numbers__line")) {
		document.querySelectorAll(".home__numbers__line").forEach(function (line) {
			gsap.to(line, {
				scrollTrigger: {
					trigger: line,
					start: "top bottom",
				},
				clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
				duration: 1,
				onStart: function () {
					const n = line.querySelector(".count");
					console.log(n);
					n.innerHTML = 0;
					setTimeout(function () {
						const updateCount = () => {
							const target = +n.getAttribute("data-target");
							const count = +n.innerText;
							const speed = 50;
							const inc = target / speed;
							if (count < target) {
								n.innerText = Math.ceil(count + inc);
								setTimeout(updateCount, speed);
							} else {
								let text = "" + target;
								text = text.replace(".", ",");
								n.innerText = text;
							}
						};
						updateCount();
					}, 500);
				},
			});
		});
	}

	if (document.querySelector(".configuration-info-link")) {
		MicroModal.init();
		document
			.querySelector(".configuration-info-link")
			.addEventListener("click", function (e) {
				e.preventDefault();
				MicroModal.show("modal-1");
			});
	}

	//header
	gsap.to(".page__header", {
		opacity: 1,
		y: 0,
		delay: document.querySelector(".home__hero") ? 1 : 0,
		duration: 2,
		ease: "Power4.easeOut",
	});

}

// MENU
setTimeout(function () {
	const menuTogglers = document.querySelectorAll(".menu-toggler");
	const mainMenu = document.querySelector("#main-menu");
	menuTogglers.forEach(function (navToggler) {
		navToggler.addEventListener("click", function () {
			if (mainMenu.classList.contains("active")) {
				if (window.smoother && window.smoother != "")
					window.smoother.paused(false);
				mainMenu.classList.remove("active");
				gsap.to(mainMenu, {
					clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
					duration: 1,
					ease: "Power3.easeOut",
				});

				resetMenu();
			} else {
				if (window.smoother && window.smoother != "")
					window.smoother.paused(true);
				mainMenu.classList.add("active");

				// loadMenu
				gsap.to(mainMenu, {
					clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
					duration: 1,
					ease: "Power3.easeOut",
				});
				gsap.to("#main-menu .lines path", {
					y: "0%",
					duration: 2,
					ease: "Power3.easeOut",
				});
				gsap.to("#main-menu .menu__item", {
					y: "0%",
					opacity: 1,
					stagger: 0.2,
					delay: 0.5,
					ease: "Power3.easeOut",
				});
				gsap.to("#main-menu .main-menu__aside", {
					y: "0%",
					opacity: 1,
					delay: 1.2,
					duration: 2,
					ease: "Power3.easeOut",
				});
			}
		});
	});
}, 500);

function resetMenu() {
	gsap.to("#main-menu .lines path", { y: "100%", ease: "Power3.easeOut" });
	gsap.to("#main-menu .menu__item", {
		y: "100%",
		opacity: 0,
		ease: "Power3.easeOut",
	});
	gsap.to("#main-menu .main-menu__aside", {
		y: "100%",
		opacity: 0,
		ease: "Power3.easeOut",
	});
}
resetMenu();

// Loader d'accueil
function gsapPreLoader() {
	// Chargement des fonctions GSAP préalables
	beforePreloader();

	// Lancement de l'animation de chargement
	let loaderTimeline = gsap.timeline({
		defaults: { duration: 0.3, ease: "power2.out" },
	});
	// Première visite
	if (
		!sessionStorage.getItem("visited") ||
		document.querySelector("body.home")
	) {
		loaderTimeline
			.to(window, {
				scrollTo: 0,
				duration: 0,
			})
			.call(gsapScrollSmootherState, ["pause"])
			.to("#page-preloader-content path:nth-child(1)", {
				clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
				duration: 0.5,
			})
			.set("#page-preloader-wrapper", {
				clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
			})
			.set("#page-preloader-content path:nth-child(2)", { fill: "#968c88" })
			.to("#page-preloader-content path:nth-child(2)", {
				clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
			})
			.set("#page-preloader-content path:nth-child(1)", {
				fill: "#2b655f",
			})
			.to("#page-preloader-content path:nth-child(2)", {
				clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
			})
			.set("#page-preloader-content path:nth-child(2)", {
				fill: "#e8c26b",
				clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)",
			})
			.to("#page-preloader-content path:nth-child(2)", {
				clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
			})
			.set("#page-preloader-content path:nth-child(1)", {
				fill: "#ffffff",
			})
			.to("#page-preloader-content path:nth-child(2)", {
				duration: 0.6,
				clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
			})
			.to(
				"#page-preloader-content svg",
				{
					duration: 1,
					scale: 0.4,
					ease: "power2.in",
				},
				"<="
			)
			.to(
				"#page-preloader-wrapper",
				{
					delay: 0.3,
					duration: 1,
					ease: "power4.out",
					clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
				},
				"<=+0.4"
			)
			.to("#page-preloader", {
				visibility: "hidden",
			});
		// after
		loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.5);
	}
	// Autres
	else {
		loaderTimeline
			// animation
			.set("#page-preloader-wrapper", {
				clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
			})
			.set("#page-preloader-content path:nth-child(1)", { fill: "#ffffff" })
			.to("#page-preloader-content path:nth-child(1)", {
				clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
			})
			.to(
				"#page-preloader-content svg",
				{
					duration: 1,
					scale: 0.4,
					ease: "power2.in",
				},
				"<="
			)
			.to(
				"#page-preloader-wrapper",
				{
					delay: 0.3,
					duration: 1,
					ease: "power4.out",
					clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
				},
				"<=+0.4"
			)
			.set("#page-preloader", {
				visibility: "hidden",
			});
		// after
		loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.2);
	}
}

// Actions avant le Loader d'accueil
function beforePreloader() {
	// Chargement des fonctions GSAP préalables
	gsapSplitText();
	gsapStickyElement();
}

// Actions après le Loader d'accueil
function afterPreloader() {
	// Chargement des fonctions GSAP résultantes
	gsapScrollSmootherState("play");
	gsapAnimations();
	setTimeout(function () {
		console.log("scrollto");
		gsapScrollTo();
	}, 500);
}

if (document.querySelector(".map-filters")) {
	if (document.body.classList.contains("promotion")) {
		document.querySelectorAll(".marker--promotion").forEach(function (marker) {
			marker.classList.add("active");
		});
		document.querySelector(".map-filter--promotion").classList.add("active");
	} else if (document.body.classList.contains("fonciere")) {
		document.querySelectorAll(".marker--fonciere").forEach(function (marker) {
			marker.classList.add("active");
		});
		document.querySelector(".map-filter--fonciere").classList.add("active");
	} else if (document.body.classList.contains("hotellerie")) {
		document.querySelectorAll(".marker--hotellerie").forEach(function (marker) {
			marker.classList.add("active");
		});
		document.querySelector(".map-filter--hotellerie").classList.add("active");
	} else {
		document.querySelectorAll(".marker").forEach(function (marker) {
			marker.classList.add("active");
		});

		// instanciate map filters
		document.querySelectorAll(".map-filter").forEach(function (e) {
			e.addEventListener("click", function () {
				let isActive = e.classList.contains("active");
				document.querySelectorAll(".map-filter").forEach(function (filter) {
					filter.classList.remove("active");
				});
				e.classList.toggle("active", !isActive);

				if (e.classList.contains("active")) {
					console.log(e.classList.contains("active"));

					document.querySelectorAll(".marker").forEach(function (marker) {
						marker.classList.remove("active");
					});
					if (e.classList.contains("map-filter--promotion")) {
						document
							.querySelectorAll(".marker--promotion")
							.forEach(function (marker) {
								marker.classList.add("active");
							});
					}
					if (e.classList.contains("map-filter--fonciere")) {
						document
							.querySelectorAll(".marker--fonciere")
							.forEach(function (marker) {
								marker.classList.add("active");
							});
					}
					if (e.classList.contains("map-filter--hotellerie")) {
						document
							.querySelectorAll(".marker--hotellerie")
							.forEach(function (marker) {
								marker.classList.add("active");
							});
					}
				} else {
					document.querySelectorAll(".marker").forEach(function (marker) {
						marker.classList.add("active");
					});
				}
			});
		});
	}
}

// init
if (document.querySelector(".home-hero-menu")) {
	gsap.set(
		".home-hero-menu a, .home-hero-menu .masks path, .home-hero-menu .lines path",
		{ y: "100%" }
	);
	gsap.set(".logo path", { opacity: 0, y: "100%" });

	if (window.innerWidth < 992) {
		setInterval(function () {
			let current = document.querySelector(".home-hero-menu a.active");
			if (current) {
				if (current.nextElementSibling.tagName == "A") {
					current.nextElementSibling.classList.add("active");
					current.classList.remove("active");
				} else {
					current.classList.remove("active");
					document
						.querySelector(".home-hero-menu a.group")
						.classList.add("active");
				}
			}
		}, 1000);
	}
}
if (document.querySelector(".home__numbers__line")) {
	gsap.set(".home__numbers__line", {
		clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
	});
}

if (document.querySelector(".espace-presse")) {
	document
		.querySelectorAll(".espace-presse .elementor-widget")
		.forEach(function (widget) {
			widget.addEventListener("click", function (e) {
				console.log("tot", e.target);
				if (e.target.nodeName != "A") {
					e.preventDefault();
					widget.querySelector(".elementor-icon-box-title a").click();
				}
			});
		});
}

if (
	document.querySelector(
		".page__footer .footer__navbar__brand .custom-svg-link > svg"
	)
) {
	const logo = document.querySelector(
		".page__footer .footer__navbar__brand .custom-svg-link > svg"
	);

	const logoTL = gsap
		.timeline({ repeat: -1, repeatDelay: 3, defaults: { duration: 1 } })
		.to(logo.querySelector(".baseline-default"), { opacity: 0 })
		.to(logo.querySelector(".baseline-fonciere"), { opacity: 1 })
		.to(
			logo.querySelector("path:nth-child(1)"),
			{
				fill: "#968c88", //fonciere
				duration: 0.2,
			},
			"<="
		)
		.to(logo.querySelector(".baseline-fonciere"), { opacity: 0 })
		.to(logo.querySelector(".baseline-promotion"), { opacity: 1 })
		.to(
			logo.querySelector("path:nth-child(1)"),
			{
				fill: "#2b655f", //promo
				duration: 0.2,
			},
			"<="
		)
		.to(logo.querySelector(".baseline-promotion"), { opacity: 0 })
		.to(logo.querySelector(".baseline-hotellerie"), { opacity: 1 })
		.to(
			logo.querySelector("path:nth-child(1)"),
			{
				fill: "#fbbe37", //hotel
				duration: 0.2,
			},
			"<="
		)
		.to(logo.querySelector(".baseline-hotellerie"), { opacity: 0 })
		.to(logo.querySelector(".baseline-default"), { opacity: 1 })
		.to(
			logo.querySelector("path:nth-child(1)"),
			{
				fill: "#282e5e", //group
				duration: 0.2,
			},
			"<="
		);
}

if (document.querySelector("#main-menu .header__navbar__menu")) {
	const mainMenu = document.querySelector("#main-menu .header__navbar__nav");
	const firstMenu = mainMenu.querySelector(".header__navbar__menu");
	mainMenu.querySelectorAll(".menu__dropdown").forEach(function (subMenu) {
		subMenu.parentNode
			.querySelector("a")
			.addEventListener("click", function (e) {
				e.preventDefault();
				firstMenu.classList.add("inactive");
				subMenu.classList.add("active");
			});
		const closeBtn = document.createElement("li");
		closeBtn.className = "close";
		closeBtn.innerHTML +=
			'<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="22px" height="36px"><path fill-rule="evenodd"  fill="rgb(255, 255, 255)" d="M21.407,32.849 L18.578,35.678 L0.900,17.1000 L18.578,0.322 L21.407,3.151 L6.557,17.1000 L21.407,32.849 Z"/></svg>';
		closeBtn.addEventListener("click", function (e) {
			e.preventDefault();
			firstMenu.classList.remove("inactive");
			subMenu.classList.remove("active");
		});
		subMenu.prepend(closeBtn);
		subMenu.classList.add("menu");
		mainMenu.append(subMenu);
	});
}
